import React from "react";
import axiosInstance from "../../api/axiosConfig";
import styles from "./Configuracion.module.css";

const CATEGORY_LABELS = {
  suscripcion: "Suscripción",
  precios: "Precios",
  general: "General",
};

export default function Configuracion() {
  const [settings, setSettings] = React.useState([]);
  const [editing, setEditing] = React.useState({}); // { [key]: tempValue }
  const [saving, setSaving] = React.useState({}); // { [key]: true }
  const [saved, setSaved] = React.useState({}); // { [key]: true } flash
  const [error, setError] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axiosInstance
      .get("/api/settings")
      .then((r) => setSettings(r.data.data || []))
      .catch(() => setError({ _global: "No se pudo cargar la configuración" }))
      .finally(() => setLoading(false));
  }, []);

  const grouped = settings.reduce((acc, s) => {
    const cat = s.category || "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const startEdit = (key, currentValue) => {
    setEditing((prev) => ({ ...prev, [key]: currentValue }));
    setError((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const cancelEdit = (key) => {
    setEditing((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleChange = (key, value) => {
    setEditing((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (setting) => {
    const value = editing[setting.key];
    if (value === undefined) return;

    if (setting.type === "number" && isNaN(Number(value))) {
      setError((prev) => ({ ...prev, [setting.key]: "Debe ser un número válido" }));
      return;
    }

    setSaving((prev) => ({ ...prev, [setting.key]: true }));
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(
        `/api/settings/${setting.key}`,
        { value },
        { headers: { Authorization: token } }
      );
      // Actualizar en local
      setSettings((prev) =>
        prev.map((s) => (s.key === setting.key ? { ...s, value } : s))
      );
      setEditing((prev) => { const n = { ...prev }; delete n[setting.key]; return n; });
      setSaved((prev) => ({ ...prev, [setting.key]: true }));
      setTimeout(() => setSaved((prev) => { const n = { ...prev }; delete n[setting.key]; return n; }), 2000);
    } catch (err) {
      setError((prev) => ({
        ...prev,
        [setting.key]: err.response?.data?.error || "Error al guardar",
      }));
    } finally {
      setSaving((prev) => { const n = { ...prev }; delete n[setting.key]; return n; });
    }
  };

  if (loading) {
    return <div className={styles.loading}>Cargando configuración...</div>;
  }

  if (error._global) {
    return <div className={styles.errorGlobal}>{error._global}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h2 className={styles.pageTitle}>Configuración del sistema</h2>
        <p className={styles.pageDesc}>
          Ajustá los precios y parámetros operativos de la plataforma. Los cambios se aplican de forma inmediata.
        </p>
      </div>

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.categoryBadge}>{CATEGORY_LABELS[cat] || cat}</span>
            <h3 className={styles.cardTitle}>{CATEGORY_LABELS[cat] || cat}</h3>
          </div>

          <div className={styles.settingsList}>
            {items.map((s) => {
              const isEditing = editing[s.key] !== undefined;
              const isSaving = saving[s.key];
              const wasSaved = saved[s.key];
              const fieldError = error[s.key];

              return (
                <div key={s.key} className={styles.settingRow}>
                  <div className={styles.settingInfo}>
                    <p className={styles.settingLabel}>{s.label}</p>
                    {s.description && (
                      <p className={styles.settingDesc}>{s.description}</p>
                    )}
                    <code className={styles.settingKey}>{s.key}</code>
                  </div>

                  <div className={styles.settingControl}>
                    {isEditing ? (
                      <div className={styles.editGroup}>
                        <input
                          className={`${styles.input} ${fieldError ? styles.inputError : ""}`}
                          type={s.type === "number" ? "number" : "text"}
                          step={s.type === "number" ? "0.01" : undefined}
                          min={s.type === "number" ? "0" : undefined}
                          value={editing[s.key]}
                          onChange={(e) => handleChange(s.key, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave(s);
                            if (e.key === "Escape") cancelEdit(s.key);
                          }}
                          autoFocus
                        />
                        {fieldError && <span className={styles.inputErrorMsg}>{fieldError}</span>}
                        <div className={styles.editActions}>
                          <button
                            className={styles.btnSave}
                            onClick={() => handleSave(s)}
                            disabled={isSaving}
                          >
                            {isSaving ? "Guardando..." : "Guardar"}
                          </button>
                          <button
                            className={styles.btnCancel}
                            onClick={() => cancelEdit(s.key)}
                            disabled={isSaving}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.valueGroup}>
                        <span className={`${styles.value} ${wasSaved ? styles.valueSaved : ""}`}>
                          {s.type === "number" && (s.category === "suscripcion" || s.category === "precios")
                            ? `S/. ${Number(s.value).toFixed(2)}`
                            : s.value}
                        </span>
                        {wasSaved && <span className={styles.savedBadge}>✓ Guardado</span>}
                        <button
                          className={styles.btnEdit}
                          onClick={() => startEdit(s.key, s.value)}
                        >
                          Editar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
